<template>
  <div class="status-history-view" id="statusHistory">
    <!-- 查询栏 -->
    <div class="compact-query-bar">
      <div class="query-section">
        <!-- 状态提示徽章 -->
        <div :class="['status-badge-tip', robotStatus === 'online' ? 'online-badge' : 'offline-badge']">
          <Icon :type="robotStatus === 'online' ? 'ios-checkmark-circle' : 'ios-close-circle'" />
          <span class="badge-text">
            {{ robotStatus === 'online' ? langMap.dashboard_onlineTip[lang] : langMap.dashboard_offlineTip[lang] }}
          </span>
        </div>
        
        <!-- 状态类型切换 -->
        <div class="monitor-type-tabs">
          <div
            v-for="type in statusTypes"
            :key="type.value"
            :class="['type-tab', { active: statusType === type.value }]"
            @click="handleStatusTypeChange(type.value)"
          >
            <span class="tab-icon">{{ type.icon }}</span>
            <span class="tab-text">{{ langMap[type.label][lang] }}</span>
          </div>
        </div>
      </div>
      
      <div class="query-filters">
        <div class="filter-row">
          <!-- 时间范围 -->
          <div class="filter-item">
            <span class="filter-label">{{ langMap.dashboard_timeRange[lang] }}</span>
            <DatePicker
              v-model="timeRange"
              :type="statusType === 'moduleLog' ? 'daterange' : 'datetimerange'"
              :placeholder="langMap.dashboard_selectTimeRangePlaceholder[lang]"
              :format="statusType === 'moduleLog' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'"
              :options="dateOptions"
              style="width: 340px"
              size="small"
            />
          </div>
          
          <!-- 设备 -->
          <div class="filter-item" v-if="statusType !== 'mileage' && statusType !== 'collision' && statusType !== 'mqttConnection' && statusType !== 'selfCheck' && statusType !== 'moduleLog'  && statusType !== 'methane'">
            <span class="filter-label">{{ langMap.dashboard_device[lang] }}</span>
            <Select
              v-model="queryParams.deviceId"
              :placeholder="langMap.dashboard_device[lang]"
              clearable
              size="small"
              style="width: 200px"
            >
              <Option
                v-for="device in deviceList"
                :key="device.deviceId"
                :value="device.deviceId"
              >
                {{ lang === 'zh' ? device.deviceNameCn : device.deviceNameEn }}
              </Option>
            </Select>
          </div>
          
          <!-- 参数名称 -->
          <div class="filter-item" v-if="statusType === 'device'">
            <span class="filter-label">{{ langMap.dashboard_paramName[lang] }}</span>
            <Select
              v-model="queryParams.paramName"
              :placeholder="langMap.dashboard_paramName[lang]"
              clearable
              size="small"
              style="width: 200px"
              :disabled="!queryParams.deviceId"
            >
              <Option
                v-for="param in paramList"
                :key="param.paramName"
                :value="param.paramName"
              >
                {{ lang === 'zh' ? param.paramNameCn : param.paramNameEn }}
              </Option>
            </Select>
          </div>
          
          <!-- 任务状态 -->
          <div class="filter-item" v-if="statusType === 'moduleLog'">
            <span class="filter-label">{{ langMap.dashboard_logTaskStatus[lang] }}</span>
            <Select
              v-model="queryParams.status"
              :placeholder="langMap.dashboard_logStatusAll[lang]"
              clearable
              size="small"
              style="width: 150px"
              @on-clear="queryParams.status = ''"
            >
              <Option value="UPLOADING">{{ langMap.dashboard_logStatusUploading[lang] }}</Option>
              <Option value="SUCCESS">{{ langMap.dashboard_logStatusSuccess[lang] }}</Option>
              <Option value="FAIL">{{ langMap.dashboard_logStatusFail[lang] }}</Option>
            </Select>
          </div>

          <!-- 模块名称 -->
          <div class="filter-item" v-if="statusType === 'moduleLog'">
            <span class="filter-label">{{ langMap.dashboard_moduleName[lang] }}</span>
            <Select
              v-model="queryParams.moduleName"
              :placeholder="langMap.dashboard_logStatusAll[lang]"
              clearable
              size="small"
              style="width: 150px"
              @on-clear="queryParams.moduleName = ''"
            >
              <Option value="all">all</Option>
              <Option value="location">location</Option>
              <Option value="control">control</Option>
              <Option value="mqtt_client">mqtt_client</Option>
              <Option value="behavior_tree_manager">behavior_tree_manager</Option>
              <Option value="task_manager">task_manager</Option>
              <Option value="monitor">monitor</Option>
              <Option value="system">system</Option>
              <Option value="media">media</Option>
              <Option value="audio">audio</Option>
              <Option value="camera">camera</Option>
              <Option value="infrared_camera">infrared_camera</Option>
              <Option value="lidar">lidar</Option>
              <Option value="navigation">navigation</Option>
              <Option value="sensor">sensor</Option>
              <Option value="mapping">mapping</Option>
              <Option value="hwid_checker">hwid_checker</Option>
              <Option value="hwid_generator">hwid_generator</Option>
            </Select>
          </div>
          
          <!-- 查询按钮 -->
          <div class="filter-actions">
            <Button type="primary" size="small" @click="handleQuery(true)" :loading="loading">
              <Icon type="ios-search" /> {{ langMap.dashboard_search[lang] }}
            </Button>
            <Button size="small" @click="handleReset">
              <Icon type="ios-refresh" /> {{ langMap.dashboard_reset[lang] }}
            </Button>
            <!-- 设备状态导出按钮 -->
            <Button v-if="statusType === 'device'" type="success" size="small" @click="handleExportDeviceStatus" :loading="deviceExportLoading">
              <Icon type="ios-download" /> {{ langMap.dashboard_export[lang] }}
            </Button>
            <!-- 机器自检按钮 -->
            <Button v-if="statusType === 'selfCheck'" type="success" size="small" @click="handleSelfCheck" :loading="selfCheckLoading">
              <Icon type="ios-build" /> {{ langMap.dashboard_startSelfCheck[lang] }}
            </Button>
            <!-- 模块日志导出按钮 -->
            <Button v-if="statusType === 'moduleLog'" type="warning" size="small" @click="handleExportLog" :loading="exportLoading">
              <Icon type="ios-download" /> {{ langMap.dashboard_exportLog[lang] }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 数据展示区域 -->
    <device-status-table
      v-if="statusType === 'device'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />
    
    <template v-else-if="statusType === 'host'">
      <network-card-history-view
        v-if="currentDisplayDeviceId === 'network_card'"
        :robot-code="robotCode"
        :loading="loading"
        :has-data="hasData"
        :empty-message="emptyMessage"
        :table-data="tableData"
        :pagination="pagination"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
      <cpu-history-table
        v-else-if="currentDisplayDeviceId === 'cpu'"
        :loading="loading"
        :has-data="hasData"
        :empty-message="emptyMessage"
        :table-data="tableData"
        :pagination="pagination"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
      <memory-history-table
        v-else-if="currentDisplayDeviceId === 'memory'"
        :loading="loading"
        :has-data="hasData"
        :empty-message="emptyMessage"
        :table-data="tableData"
        :pagination="pagination"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
      <disk-history-table
        v-else-if="currentDisplayDeviceId === 'disk'"
        :loading="loading"
        :has-data="hasData"
        :empty-message="emptyMessage"
        :table-data="tableData"
        :pagination="pagination"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
      <!-- 如果未选择设备或选择其他，显示空状态或默认组件 -->
      <div v-else class="empty-state">
        <div class="empty-icon-wrapper">
          <div class="hologram-ring"></div>
          <div class="empty-icon">🖥️</div>
        </div>
        <div class="empty-text">{{ langMap.dashboard_selectDevice[lang] }}</div>
      </div>
    </template>
    
    <!-- 里程状态查询 -->
    <mileage-history-table
      v-else-if="statusType === 'mileage'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <!-- 甲烷查询 -->
    <methane-history-table
      v-else-if="statusType === 'methane'"
        :loading="loading"
        :has-data="hasData"
        :empty-message="emptyMessage"
        :table-data="tableData"
        :pagination="pagination"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange" 
      />

    <!-- 碰撞检测查询 -->
    <collision-history-table
      v-else-if="statusType === 'collision'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <!-- 上下线状态查询 -->
    <mqtt-connection-history-table
      v-else-if="statusType === 'mqttConnection'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <!-- 机器自检查询 -->
    <self-check-history-table
      v-else-if="statusType === 'selfCheck'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <!-- 模块日志查询 -->
    <module-log-history-table
      v-else-if="statusType === 'moduleLog'"
      :loading="loading"
      :has-data="hasData"
      :empty-message="emptyMessage"
      :table-data="tableData"
      :pagination="pagination"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />
    
    <!-- 导出进度浮窗 -->
    <transition name="float-fade">
      <div v-if="showDownloadModal" class="export-float-window">
        <div class="float-content">
          <div class="float-header">
            <div class="header-glow"></div>
            <Icon type="ios-download" class="float-icon" />
            <span class="float-title">{{ langMap.dashboard_exporting[lang] }}</span>
          </div>
          <div class="progress-container">
            <div class="progress-track">
              <div 
                v-if="downloadProgress >= 0"
                class="progress-bar" 
                :style="{ width: downloadProgress + '%' }"
              >
                <div class="progress-glow"></div>
              </div>
              <div 
                v-else
                class="progress-bar indeterminate"
              >
                <div class="progress-glow"></div>
              </div>
            </div>
            <div class="progress-text">
              {{ downloadProgress >= 0 ? downloadProgress + '%' : '...' }}
            </div>
          </div>
        </div>
        <div class="float-border"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/api'
import { buildQueryParams } from '@/libs/util'
import langMap from '@/utils/language'
import { mapState } from 'vuex';
import { getMvpUrlMap } from '@/utils/websocket'
import DeviceStatusTable from './DeviceStatusTable.vue'
import NetworkCardHistoryView from './NetworkCardHistoryView.vue'
import CpuHistoryTable from './CpuHistoryTable.vue'
import MemoryHistoryTable from './MemoryHistoryTable.vue'
import DiskHistoryTable from './DiskHistoryTable.vue'
import MileageHistoryTable from './MileageHistoryTable.vue'
import MethaneHistoryTable from './MethaneHistoryTable.vue' 
import CollisionHistoryTable from './CollisionHistoryTable.vue'
import MqttConnectionHistoryTable from './MqttConnectionHistoryTable.vue'
import SelfCheckHistoryTable from './SelfCheckHistoryTable.vue'
import ModuleLogHistoryTable from './ModuleLogHistoryTable.vue'

export default {
  name: 'StatusHistoryView',
  components: {
    DeviceStatusTable,
    NetworkCardHistoryView,
    CpuHistoryTable,
    MemoryHistoryTable,
    DiskHistoryTable,
    MileageHistoryTable,
    MethaneHistoryTable,
    CollisionHistoryTable,
    MqttConnectionHistoryTable,
    SelfCheckHistoryTable,
    ModuleLogHistoryTable
  },
  props: {
    robotCode: {
      type: String,
      required: true
    },
    robotStatus: {
      type: String,
      default: 'offline' // 'online' 或 'offline'
    }
  },
  data() {
    return {
      loading: false,
      selfCheckLoading: false,
      exportLoading: false,
      deviceExportLoading: false,
      downloadProgress: 0,
      showDownloadModal: false,
      langMap: langMap,
      timeRange: [],
      hasData: false,
      emptyMessage: '',
      // 状态类型
      statusType: 'device',
      statusTypes: [
        { value: 'device', label: 'dashboard_deviceStatus', icon: '📟' },
        { value: 'host', label: 'dashboard_hostStatus', icon: '🖥️' },
        { value: 'mileage', label: 'dashboard_mileageStatus', icon: '🛣️' },
        { value: 'methane', label: 'dashboard_methane', icon: '🔥' },
        { value: 'collision', label: 'dashboard_collisionDetection', icon: '⚠️' },
        { value: 'mqttConnection', label: 'dashboard_onlineOfflineStatus', icon: '⏻' },
        { value: 'selfCheck', label: 'dashboard_selfCheck', icon: '🔧' },
        { value: 'moduleLog', label: 'dashboard_moduleLog', icon: '📋' }
      ],
      queryParams: {
        robotCode: this.robotCode,
        startTime: null,
        endTime: null,
        deviceId: '',
        paramName: '',
        status: '', // 模块日志任务状态
        moduleName: '', // 模块名称
        index: 1,
        size: 20
      },
      currentDisplayDeviceId: '', // 当前显示的设备ID，用于控制组件切换
      tableData: [],
      pagination: {
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      },
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      // 设备列表
      deviceList: [],
      // 参数列表
      paramList: []
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    // 日期选择器配置
    dateOptions() {
      return {
        disabledDate: (date) => {
          // 禁用未来时间
          const now = new Date();
          if (date > now) {
            return true;
          }

          // 禁用2个月以前的时间
          const twoMonthsAgo = new Date();
          twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
          if (date < twoMonthsAgo) {
            return true;
          }

          // 如果已经选择了开始或结束时间，限制范围为7天
          if (this.timeRange && this.timeRange.length > 0) {
            const selectedDate = this.timeRange[0];
            if (selectedDate) {
              const maxDiff = 7 * 24 * 3600 * 1000; // 7天的毫秒数
              const diff = Math.abs(date.getTime() - selectedDate.getTime());
              if (diff > maxDiff) {
                return true;
              }
            }
          }

          return false;
        },
        shortcuts: this.statusType === 'moduleLog'
          ? []
          : [
              {
                text: this.langMap.dashboard_last1Hour[this.lang],
                value() {
                  const end = new Date();
                  const start = new Date();
                  start.setTime(start.getTime() - 3600 * 1000);
                  return [start, end];
                },
              },
              {
                text: this.langMap.dashboard_last3Hours[this.lang],
                value() {
                  const end = new Date();
                  const start = new Date();
                  start.setTime(start.getTime() - 3600 * 1000 * 3);
                  return [start, end];
                },
              },
              {
                text: this.langMap.dashboard_last6Hours[this.lang],
                value() {
                  const end = new Date();
                  const start = new Date();
                  start.setTime(start.getTime() - 3600 * 1000 * 6);
                  return [start, end];
                },
              },
              {
                text: this.langMap.dashboard_last24Hours[this.lang],
                value() {
                  const end = new Date();
                  const start = new Date();
                  start.setTime(start.getTime() - 3600 * 1000 * 24);
                  return [start, end];
                },
              },
              {
                text: this.langMap.dashboard_last7Days[this.lang],
                value() {
                  const end = new Date();
                  const start = new Date();
                  start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                  return [start, end];
                },
              },
            ],
      };
    },
  },
  watch: {
    robotCode(newCode) {
      this.queryParams.robotCode = newCode;
      this.handleReset();
    },
    timeRange(newVal) {
      // 当 timeRange 通过 v-model 变化时，重新计算时间戳
      if (newVal && Array.isArray(newVal) && newVal.length === 2 && newVal[0] && newVal[1]) {
        this.handleTimeChange(newVal);
      } else {
        // DatePicker被清空时，清空时间戳
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    },
    // 监听设备变化，加载对应的参数列表
    'queryParams.deviceId'(newDeviceId) {
      // 清空参数名称
      this.queryParams.paramName = '';
      this.paramList = [];
      if (newDeviceId && this.statusType === 'device') {
        this.loadParamList(newDeviceId);
      }
    }
  },
  mounted() {
    // 默认设置最近3小时
    this.emptyMessage = this.langMap.dashboard_selectTimeAndSearch[this.lang];
    const end = new Date();
    const start = new Date();
    start.setTime(start.getTime() - 3600 * 1000 * 3);
    this.timeRange = [start, end];
    this.handleTimeChange(this.timeRange);
    
    // 加载设备列表
    this.loadDeviceList();
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // 移除窗口resize监听
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleTimeChange(dates) {
      if (dates && dates.length === 2) {
        // 验证时间范围是否超过7天
        const diff = dates[1].getTime() - dates[0].getTime();
        const maxDiff = 7 * 24 * 3600 * 1000; // 7天
        
        if (diff > maxDiff) {
          this.$Message.warning(this.langMap.dashboard_timeRangeMaxOneWeek[this.lang]);
          this.timeRange = [];
          this.queryParams.startTime = null;
          this.queryParams.endTime = null;
          return;
        }
        
        this.queryParams.startTime = dates[0].getTime();
        this.queryParams.endTime = dates[1].getTime();
      } else {
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    },
    
    handleQuery(resetPage = false) {
      if (!this.queryParams.startTime || !this.queryParams.endTime) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectTimeRange[this.lang]);
        return;
      }

      // 只有主动查询时才重置到第1页和默认每页数量
      if (resetPage) {
        this.queryParams.index = 1;
        this.queryParams.size = 20;
        this.pagination.current = 1;
        this.pagination.size = 20;
      }

      // 更新当前显示的设备ID，触发组件切换
      this.currentDisplayDeviceId = this.queryParams.deviceId;

      this.loading = true;
      this.emptyMessage = this.langMap.dashboard_querying[this.lang];

      
      let apiPath;
      let queryString = buildQueryParams(this.queryParams);
      if (this.statusType === 'device') {
        apiPath = `notice-server/monitor/record/running/param/page?${queryString}`;
      } else if (this.statusType === 'mileage') {
        // 里程状态查询
        apiPath = `notice-server/monitor/record/robotStatus/page?${queryString}`;
      } else if (this.statusType === 'methane') {
        // 甲烷状态查询
        apiPath = `notice-server/monitor/record/methane/page?${queryString}`;
      } else if (this.statusType === 'collision') {
        // 碰撞检测查询
        apiPath = `notice-server/monitor/record/collision/page?${queryString}`;
      } else if (this.statusType === 'mqttConnection') {
        // 上下线状态（MQTT连接）
        apiPath = `notice-server/monitor/record/mqttConnection/page?${queryString}`;
      } else if (this.statusType === 'selfCheck') {
        // 机器自检查询
        apiPath = `notice-server/monitor/record/selfCheck/page?${queryString}`;
      } else if (this.statusType === 'moduleLog') {
        // 模块日志导出任务查询（新接口字段：pageIndex/pageSize/startDate/endDate/moduleName）
        const moduleLogParams = {
          robotCode: this.queryParams.robotCode,
          pageIndex: this.queryParams.index,
          pageSize: this.queryParams.size,
          status: this.queryParams.status,
          moduleName: this.queryParams.moduleName, // 未选择时默认传递空
          // 查询要求使用时间戳：开始日 00:00:00，结束日 23:59:59
          startDate: this.getDayStartTimestamp(this.timeRange && this.timeRange[0]),
          endDate: this.getDayEndTimestamp(this.timeRange && this.timeRange[1])
        };
        queryString = buildQueryParams(moduleLogParams);
        apiPath = `notice-server/log/export/list?${queryString}`;
      } else {
        // 主机状态查询
        if (this.queryParams.deviceId === 'cpu') {
          apiPath = `notice-server/monitor/record/cpu/page?${queryString}`;
        } else if (this.queryParams.deviceId === 'memory') {
          apiPath = `notice-server/monitor/record/memory/page?${queryString}`;
        } else if (this.queryParams.deviceId === 'disk') {
          apiPath = `notice-server/monitor/record/disk/page?${queryString}`;
        } else {
          // 默认为网卡
          apiPath = `notice-server/monitor/record/nic/page?${queryString}`;
        }
      }
      
      api.getData(apiPath, this).then(res => {
        this.loading = false;
        if (res.state !== 0) {
          const responseData = (res && res.data) || {};
          // 兼容两种返回结构：
          // 1) { data: { total, rows } }
          // 2) { data: { data: { total, rows } } }
          const pageData = (responseData.rows && responseData) || (responseData.data && responseData.data.rows && responseData.data) || {};
          let rows = pageData.rows || [];
          
          if (this.statusType === 'host') {
             // 解析主机状态数据 (网卡或CPU)
              rows = rows.map(row => {
                let parsedData = {};
                try {
                  if (row.data) {
                    const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                    parsedData = parsed || {};
                  }
                } catch (e) {
                  console.error('解析数据失败', e);
                }
                
                if (this.queryParams.deviceId === 'cpu') {
                   return { ...row, ...parsedData };
                } else if (this.queryParams.deviceId === 'memory') {
                   // 内存
                   return { ...row, ...parsedData };
                } else if (this.queryParams.deviceId === 'disk') {
                   // 磁盘
                   return { ...row, ...parsedData };
                } else {
                   // 网卡
                   return { ...row, nicList: parsedData.nicList || [] };
                }
              });
          } else if (this.statusType === 'mileage') {
            // 解析里程状态数据
            rows = rows.map(row => {
              let parsedData = {};
              try {
                if (row.data) {
                  const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                  parsedData = parsed || {};
                }
              } catch (e) {
                console.error('解析里程数据失败', e);
              }
              
              return { 
                ...row, 
                totalPowerOnSeconds: parsedData.totalPowerOnSeconds,
                currentPowerOnSeconds: parsedData.currentPowerOnSeconds,
                totalDrivingDistance: parsedData.totalDrivingDistance,
                currentDrivingDistance: parsedData.currentDrivingDistance,
                totalPowerOnTimes: parsedData.totalPowerOnTimes,
                totalPowerOnDays: parsedData.totalPowerOnDays
              };
            });
          } else if (this.statusType === 'collision') {
            // 解析碰撞检测数据
            rows = rows.map(row => {
              let parsedData = {};
              try {
                if (row.data) {
                  const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                  parsedData = parsed || {};
                }
              } catch (e) {
                console.error('解析碰撞数据失败', e);
              }
              
              return { 
                ...row, 
                dataTime: parsedData.dataTime,
                time: parsedData.time,
                type: parsedData.type,
                acceleration: parsedData.acceleration,
                accelerationX: parsedData.accelerationX,
                accelerationY: parsedData.accelerationY,
                accelerationZ: parsedData.accelerationZ
              };
            });
          } else if (this.statusType === 'selfCheck') {
            // 解析机器自检数据 - 使用统一的处理方法
            rows = this.processSelfCheckData(rows);
          } else if (this.statusType === 'moduleLog') {
            // 模块日志查询新结构：直接使用 rows 字段
            rows = rows.map(row => {
              return { 
                ...row, 
                taskId: row.taskId || '-',
                modules: row.modules || '',
                createTime: row.createTime || '',
                finishTime: row.finishTime || '',
                relativePath: row.relativePath || '',
                failReason: row.failReason || ''
              };
            });
          } else if (this.statusType === 'methane') {
            // 解析甲烷状态数据
            // 解析甲烷状态数据
            rows = rows.map(row => {
              let parsedData = {};
              try {
                if (row.data) {
                  const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                  parsedData = parsed || {};
                }
              } catch (e) {
                console.error('解析甲烷数据失败', e);
              }
              return { ...row, ...parsedData };
            });
          }
          
          this.tableData = rows;
          // 服务器如果没有返回 current，就使用请求的 index
          const currentPage = pageData.current || pageData.pageIndex || this.queryParams.index;
          const totalPages = pageData.pages || Math.ceil((pageData.total || 0) / this.queryParams.size);
          
          // 使用 $set 确保响应式更新
          this.$set(this, 'pagination', {
            total: pageData.total || 0,
            current: currentPage,
            size: pageData.size || pageData.pageSize || this.queryParams.size,
            pages: totalPages
          });
          this.hasData = this.tableData.length > 0;
          if (!this.hasData) {
            this.emptyMessage = this.langMap.empty[this.lang];
          }
        } else {
          this.hasData = false;
          this.emptyMessage = res.msg || this.langMap.dashboard_queryFailed[this.lang];
          this.$Message.error(res.msg || this.langMap.dashboard_queryFailed[this.lang]);
        }
      }).catch(error => {
        this.loading = false;
        this.hasData = false;
        this.emptyMessage = this.langMap.dashboard_queryFailed[this.lang];
        this.$Message.error(this.langMap.dashboard_queryFailed[this.lang] + ': ' + (error.message || ''));
      });
    },
    
    handlePageChange(page) {
      this.queryParams.index = page;
      // 立即更新 pagination.current 以保持 UI 一致
      this.pagination.current = page;
      this.handleQuery();
    },
    
    handlePageSizeChange(size) {
      this.queryParams.size = size;
      this.queryParams.index = 1;
      this.pagination.current = 1;
      this.handleQuery();
    },
    
    // 加载设备列表
    loadDeviceList() {
      if (this.statusType === 'host') {
        // 主机状态，设备固定为网卡、CPU、内存、磁盘
        this.deviceList = [
          {
            deviceId: 'network_card',
            deviceNameCn: '网卡',
            deviceNameEn: 'Network Card'
          },
          {
            deviceId: 'cpu',
            deviceNameCn: 'CPU',
            deviceNameEn: 'CPU'
          },
          {
            deviceId: 'memory',
            deviceNameCn: '内存',
            deviceNameEn: 'Memory'
          },
          {
            deviceId: 'disk',
            deviceNameCn: '磁盘',
            deviceNameEn: 'Disk'
          }
        ];
        // 默认选中第一个
        if (!this.queryParams.deviceId) {
          this.queryParams.deviceId = 'network_card';
          this.currentDisplayDeviceId = 'network_card';
        }
        return;
      } else if (this.statusType === 'mileage' || this.statusType === 'collision') {
        // 里程状态和碰撞检测，不需要设备选择
        this.deviceList = [];
        this.queryParams.deviceId = '';
        this.currentDisplayDeviceId = '';
        return;
      } else if (this.statusType === 'selfCheck' || this.statusType === 'moduleLog') {
        // 机器自检和模块日志，不需要设备选择
        this.deviceList = [];
        this.queryParams.deviceId = '';
        this.currentDisplayDeviceId = '';
        return;
      }

      api.getData('system-server/monitor/dict/device/list', this).then(res => {
        if (res.state == 1) {
          this.deviceList = (res.data || []).filter(j => j.deviceType === 1);
        }
      }).catch(err => {
        console.error('加载设备列表失败', err);
      });
    },
    
    // 加载参数列表
    loadParamList(deviceId) {
      if (!deviceId) return;
      
      api.getData('system-server/monitor/dict/param/list?deviceId='+deviceId, this).then(res => {
        if (res.state == 1) {
          this.paramList = res.data || [];
        }
      }).catch(err => {
        console.error('加载参数列表失败', err);
      });
    },
    
    handleReset() {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 3);
      this.timeRange = [start, end];
      this.handleTimeChange(this.timeRange);
      this.queryParams.deviceId = '';
      this.queryParams.paramName = '';
      this.queryParams.status = '';
      this.queryParams.moduleName = '';
      this.queryParams.index = 1;
      this.queryParams.size = 20;
      this.hasData = false;
      this.tableData = [];
      this.pagination = {
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      };
      this.emptyMessage = this.langMap.dashboard_selectTimeAndSearch[this.lang];
    },
    
    
    // 处理窗口大小变化
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    
    // 切换状态类型
    handleStatusTypeChange(type) {
      if (this.statusType === type) return;
      this.statusType = type;
      
      // 重置查询条件
      this.handleReset();
      
      // 重新加载设备列表
      this.loadDeviceList();
    },
    
    // 机器自检
    handleSelfCheck() {
      this.selfCheckLoading = true;
      
      // 构建请求参数 - 根据API文档，只需要 code 参数
      const params = {
        code: this.robotCode
      };
      
      // 调用自检接口
      api.postData('notice-server/self-check/trigger', params, this).then(res => {
        this.selfCheckLoading = false;
        if (res.state !== 0) {
          const requestId = res.data;
          this.$Message.success(this.langMap.dashboard_selfCheckStarted[this.lang]);
          // 自检成功后延迟5秒查询，等待机器人上报结果
          setTimeout(() => {
            this.handleQuery();
          }, 5000);
        } else {
          this.$Message.error(res.msg || this.langMap.dashboard_selfCheckFailed[this.lang]);
        }
      }).catch(error => {
        this.selfCheckLoading = false;
        this.$Message.error(this.langMap.dashboard_selfCheckFailed[this.lang] + ': ' + (error.message || ''));
      });
    },
    
    // 处理自检数据（统一的处理方法）
    processSelfCheckData(rows) {
      return rows.map(row => {
        let parsedData = {};
        try {
          // 优先使用 translatedData，包含中英文设备名称
          const dataSource = row.translatedData || row.data;
          if (dataSource) {
            const parsed = typeof dataSource === 'string' ? JSON.parse(dataSource) : dataSource;
            parsedData = parsed || {};
            
            // 处理 deviceNameCn 为 "summary of self_check" 的情况
            if (parsedData.items && Array.isArray(parsedData.items)) {
              parsedData.items = parsedData.items.map(item => {
                if (item.deviceNameCn === 'summary of self_check') {
                  return {
                    ...item,
                    deviceNameCn: '总结'
                  };
                }
                return item;
              });
            }
          }
        } catch (e) {
          console.error('解析自检数据失败', e);
        }
        
        // 根据 status 字段判断自检状态
        // status: 1-等待上报，2-已完成，3-失败
        const isCompleted = row.status === 2;
        const isFailed = row.status === 3;
        const checkStatus = isFailed ? 'failed' : (isCompleted ? 'completed' : 'pending');
        
        // 统计检查结果
        let resultSummary = '-';
        let resultBadges = []; // 新增：结构化的徽章数据
        
        if (isCompleted && parsedData.items && parsedData.items.length > 0) {
          const levelCount = { ok: 0, warn: 0, error: 0, fatal: 0 };
          parsedData.items.forEach(item => {
            if (item.level === 0) levelCount.ok++;
            else if (item.level === 1) levelCount.warn++;
            else if (item.level === 2) levelCount.error++;
            else if (item.level === 3) levelCount.fatal++;
          });
          
          // 生成结果摘要文本
          const parts = [];
          if (levelCount.ok > 0) parts.push(`${this.langMap.dashboard_selfCheckOk[this.lang]}: ${levelCount.ok}`);
          if (levelCount.warn > 0) parts.push(`${this.langMap.dashboard_selfCheckWarn[this.lang]}: ${levelCount.warn}`);
          if (levelCount.error > 0) parts.push(`${this.langMap.dashboard_selfCheckError[this.lang]}: ${levelCount.error}`);
          if (levelCount.fatal > 0) parts.push(`${this.langMap.dashboard_selfCheckFatal[this.lang]}: ${levelCount.fatal}`);
          resultSummary = parts.join(', ');
          
          // 生成结构化的徽章数据（用于表格显示）
          if (levelCount.ok > 0) {
            resultBadges.push({
              type: 'ok',
              icon: 'ios-checkmark-circle',
              label: this.langMap.dashboard_selfCheckOk[this.lang],
              count: levelCount.ok
            });
          }
          if (levelCount.warn > 0) {
            resultBadges.push({
              type: 'warn',
              icon: 'ios-warning',
              label: this.langMap.dashboard_selfCheckWarn[this.lang],
              count: levelCount.warn
            });
          }
          if (levelCount.error > 0) {
            resultBadges.push({
              type: 'error',
              icon: 'ios-close-circle',
              label: this.langMap.dashboard_selfCheckError[this.lang],
              count: levelCount.error
            });
          }
          if (levelCount.fatal > 0) {
            resultBadges.push({
              type: 'fatal',
              icon: 'ios-alert',
              label: this.langMap.dashboard_selfCheckFatal[this.lang],
              count: levelCount.fatal
            });
          }
        }
        
        return { 
          ...row,
          checkTime: row.checkTime || parsedData.dataTime || row.createTime,
          status: checkStatus,
          result: resultSummary,
          resultBadges: resultBadges, // 新增字段
          items: parsedData.items || [],
          rawData: parsedData
        };
      });
    },
    
    // 导出模块日志
    handleExportLog() {
      if (!this.queryParams.startTime || !this.queryParams.endTime) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectTimeRange[this.lang]);
        return;
      }

      // 检查时间范围是否超过3天
      // 对于日期范围，计算实际的天数差（包含开始和结束日期）
      const startDate = new Date(this.queryParams.startTime);
      const endDate = new Date(this.queryParams.endTime);

      // 重置时间为00:00:00来计算准确的天数差
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.round((endDate - startDate) / (24 * 3600 * 1000)) + 1; // +1 包含结束日期当天

      if (daysDiff > 3) {
        this.$Message.warning(this.langMap.dashboard_exportTimeRangeMax3Days[this.lang]);
        return;
      }

      if (!this.queryParams.moduleName) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectModule[this.lang]);
        return;
      }

      this.exportLoading = true;

      // 构建请求参数
      const params = {
        robotCode: this.robotCode,
        startDate: this.formatDateToYMD(this.timeRange && this.timeRange[0]),
        endDate: this.formatDateToYMD(this.timeRange && this.timeRange[1]),
        moduleName: this.queryParams.moduleName,
      };

      // 调用导出接口
      api.postData('notice-server/log/export/request', params, this).then(res => {
        this.exportLoading = false;
        if (res.state !== 0) {
          this.$Message.success(this.langMap.dashboard_exportSuccess[this.lang]);
          // 导出成功后自动查询
          setTimeout(() => {
            this.handleQuery();
          }, 1000);
        } else {
          this.$Message.error(res.msg || this.langMap.dashboard_exportFailed[this.lang]);
        }
      }).catch(error => {
        this.exportLoading = false;
        this.$Message.error(this.langMap.dashboard_exportFailed[this.lang] + ': ' + (error.message || ''));
      });
    },
    
    // 导出设备状态
    handleExportDeviceStatus() {
      if (!this.queryParams.startTime || !this.queryParams.endTime) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectTimeRange[this.lang]);
        return;
      }
      
      if (!this.queryParams.deviceId) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectDevice[this.lang]);
        return;
      }
      
      // 显示进度弹窗
      this.showDownloadModal = true;
      this.downloadProgress = 0;
      this.deviceExportLoading = true;
      
      // 构建导出参数（不包含分页参数）
      const exportParams = {
        robotCode: this.queryParams.robotCode,
        startTime: this.queryParams.startTime,
        endTime: this.queryParams.endTime,
        deviceId: this.queryParams.deviceId
      };
      
      // 如果选择了参数名称，也加入导出参数
      if (this.queryParams.paramName) {
        exportParams.paramName = this.queryParams.paramName;
      }
      
      // 构建查询字符串
      const queryString = buildQueryParams(exportParams);
      const apiPath = `notice-server/monitor/record/running/param/export?${queryString}`;
      
      // 生成文件名：使用翻译并添加机器人编号
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const fileNamePrefix = this.langMap.dashboard_deviceStatusData[this.lang];
      const fileName = `${fileNamePrefix}_${this.robotCode}_${dateStr}_${timeStr}.xlsx`;
      
      // 使用支持进度监听的下载方法
      api.downloadWithProgress(apiPath, fileName, (percent, loaded, total) => {
        // 更新进度
        console.log('下载进度回调:', { percent, loaded, total });
        if (percent === -1) {
          // 不确定进度，显示加载动画
          this.downloadProgress = -1;
        } else {
          this.downloadProgress = percent;
        }
      }).then(() => {
        this.deviceExportLoading = false;
        this.downloadProgress = 100;
        // 延迟关闭弹窗，让用户看到100%
        setTimeout(() => {
          this.showDownloadModal = false;
          this.$Message.success(this.langMap.dashboard_exportSuccess[this.lang]);
        }, 500);
      }).catch(error => {
        this.deviceExportLoading = false;
        this.showDownloadModal = false;
        this.$Message.error(this.langMap.dashboard_exportFailed[this.lang] + ': ' + (error.message || ''));
      });
    },

    formatDateToYMD(dateValue) {
      if (!dateValue) return '';
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    },

    getDayStartTimestamp(dateValue) {
      if (!dateValue) return '';
      const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    },

    getDayEndTimestamp(dateValue) {
      if (!dateValue) return '';
      const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      date.setHours(23, 59, 59, 999);
      return date.getTime();
    }
  }
}
</script>

<style lang="less">
#monitorContainer {
  .status-history-view {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
    position: relative;
    overflow: hidden;
  }
}

// 导出进度浮窗
.export-float-window {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  min-width: 320px;
  
  .float-content {
    position: relative;
    background: linear-gradient(135deg, rgba(0, 10, 30, 0.95) 0%, rgba(0, 20, 50, 0.95) 100%);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 24px 28px;
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.6),
      0 0 0 1px rgba(0, 240, 255, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    
    .float-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      position: relative;
      
      .header-glow {
        position: absolute;
        left: -10px;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 24px;
        background: linear-gradient(180deg, #00f0ff 0%, #0080ff 100%);
        border-radius: 2px;
        box-shadow: 0 0 12px rgba(0, 240, 255, 0.8);
        animation: pulseGlow 2s ease-in-out infinite;
      }
      
      .float-icon {
        font-size: 24px;
        color: #00f0ff;
        filter: drop-shadow(0 0 8px rgba(0, 240, 255, 0.6));
        animation: iconPulse 2s ease-in-out infinite;
      }
      
      .float-title {
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
        letter-spacing: 0.5px;
      }
    }
    
    .progress-container {
      display: flex;
      align-items: center;
      gap: 16px;
      
      .progress-track {
        flex: 1;
        height: 8px;
        background: rgba(0, 0, 0, 0.4);
        border-radius: 4px;
        overflow: hidden;
        position: relative;
        border: 1px solid rgba(0, 240, 255, 0.2);
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(0, 240, 255, 0.1) 50%, 
            transparent 100%
          );
          animation: trackShimmer 2s linear infinite;
        }
        
        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #00f0ff 0%, #0080ff 100%);
          border-radius: 4px;
          position: relative;
          transition: width 0.3s ease;
          
          .progress-glow {
            position: absolute;
            top: 0;
            right: 0;
            width: 40px;
            height: 100%;
            background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 100%);
            filter: blur(4px);
            animation: glowMove 1.5s ease-in-out infinite;
          }
          
          &.indeterminate {
            width: 100%;
            background: linear-gradient(90deg, 
              transparent 0%, 
              rgba(0, 240, 255, 0.4) 25%, 
              rgba(0, 240, 255, 0.8) 50%, 
              rgba(0, 240, 255, 0.4) 75%, 
              transparent 100%
            );
            background-size: 200% 100%;
            animation: indeterminateMove 1.5s ease-in-out infinite;
          }
        }
      }
      
      .progress-text {
        font-family: 'Courier New', monospace;
        font-size: 18px;
        font-weight: 700;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
        min-width: 60px;
        text-align: right;
      }
    }
  }
  
  .float-border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(135deg, 
      rgba(0, 240, 255, 0.5) 0%, 
      rgba(0, 120, 255, 0.3) 50%, 
      rgba(0, 240, 255, 0.5) 100%
    );
    background-size: 200% 200%;
    animation: borderFlow 3s linear infinite;
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

// 浮窗动画
.float-fade-enter-active, .float-fade-leave-active {
  transition: all 0.3s ease;
}

.float-fade-enter, .float-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

// 关键帧动画
@keyframes pulseGlow {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 12px rgba(0, 240, 255, 0.8);
  }
  50% {
    opacity: 0.6;
    box-shadow: 0 0 20px rgba(0, 240, 255, 1);
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 8px rgba(0, 240, 255, 0.6));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 12px rgba(0, 240, 255, 1));
  }
}

@keyframes trackShimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes glowMove {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes indeterminateMove {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes borderFlow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
